/**
Service for fetching an image of a borehole.

Return an image of the borehole in SVG format.
*/

import { GeoWebServiceClient } from './GeoWebServiceClient'

/**
 * Web service for fetching an image for a given borehole.
 * The following parameter is required: 'BoreholeNo' ( @see setBoreholeNo ).
 */
export class Borehole extends GeoWebServiceClient {
  static get URL (): string { return 'https://data.geo.dk/api/v3/borehole' }

  /**
   * Constructor.
   * @param token The Geo auth token.
   */
  constructor (token: string) {
    const defaultParams = {
      format: 'image/svg+xml'
    }
    super(Borehole.URL, token, defaultParams)
  }

  /**
   * [Required*] Setter for the borehole id.
   * *: This parameter or BoreholeNo must be set.
   * @param boreholeId The borehole id.
   */
  setBoreholeId (boreholeId: number): void {
    this.addParam('BoreholeId', boreholeId.toString())
  }

  /**
   * [Required*] Setter for the borehole number.
   * *: This paremeter or BoreholeId must be set.
   * @param boreholeNo The borehole number.
   */
  setBoreholeNo (boreholeNo: string): void {
    this.addParam('BoreholeNo', boreholeNo)
  }

  /**
   * Validates that required params are given.
   */
  private validateParams (): void {
    if (!(this.hasParam('BoreholeNo') || this.hasParam('BoreholeId'))) {
      throw new Error('Parameter mismatch: The boreholeNo or boreholeId param must be set.')
    }
  }

  /**
   * Calls the web service.
   * @returns A promise bearing the borehole image.
   */
  async fetchBorehole (): Promise<string> {
    this.validateParams()
    const response: Response = await fetch(this.url(), { headers: { Authorization: `Bearer ${this.token}` } })
    if (!response.ok) {
      throw new Error('Got invalid HTTP response.')
    }

    return await response.text()
  }
}
